import { ref, toValue, watch, type MaybeRefOrGetter } from 'vue'
import type { AlgoliaLocation } from '~/composables/types/algolia'
import { useAlgoliaIndexNames } from '~/composables/algolia/useAlgoliaIndexNames'
import { localitySlugForAlgoliaOptionalFilter } from '~/utils/localitySlugMatch'

export const useAlgoliaLocalities = async (
  initialLocationSlug: MaybeRefOrGetter<string> = '',
  immediate = true
) => {
  const searchQuery = ref('')
  const locationSlug = ref(toValue(initialLocationSlug))

  watch(
    () => toValue(initialLocationSlug),
    (v) => {
      locationSlug.value = v ?? ''
    }
  )

  const defaultReqOpts = {
    similarQuery: '',
    clickAnalytics: true,
    hitsPerPage: 20,
  }
  const { locations: locationsIndex } = useAlgoliaIndexNames()

  const { result: localitiesAlgolia, search } =
    useAlgoliaSearch<AlgoliaLocation>(locationsIndex)

  const { execute } = await useAsyncData(
    locationsIndex,
    () => {
      const slugOpt = localitySlugForAlgoliaOptionalFilter(locationSlug.value)
      return search({
        query: searchQuery.value,
        requestOptions: {
          ...defaultReqOpts,
          ...(slugOpt ? { optionalFilters: [`slug:${slugOpt}`] } : {}),
          aroundLatLngViaIP:
            !slugOpt && import.meta.client && !searchQuery.value,
        },
      })
    },
    {
      immediate,
      deep: true,
      watch: [locationSlug, searchQuery],
    }
  )

  async function searchLocalities(query: string) {
    searchQuery.value = query
    return await execute()
  }

  async function searchLocalitiesWithOptionalLocationFilter(slug: string) {
    locationSlug.value = slug
    return await execute()
  }

  return {
    localitiesAlgolia,
    setLocationSlug: (slugVal: string) => {
      locationSlug.value = slugVal
    },
    searchLocalities,
    searchLocalitiesWithOptionalLocationFilter,
  }
}
